import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import "./ManageParticipantsPage.css"; // Asegúrate de que este archivo CSS exista

function ManageParticipantsPage() {
  const [participants, setParticipants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState(""); // Mensajes de éxito
  const [showErrorPopup, setShowErrorPopup] = useState(false); // Estado para pop-up de error
  const [errorPopupMessage, setErrorPopupMessage] = useState(""); // Mensaje de pop-up de error

  // Estados para Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [participantsPerPage] = useState(10); // Mostrar 10 participantes por página

  // Estados para Búsqueda y Filtro
  const [searchTerm, setSearchTerm] = useState("");
  const [searchColumn, setSearchColumn] = useState("full_name"); // Buscar por nombre completo

  const { token } = useAuth();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();

  // Función para obtener todos los participantes del backend
  const fetchParticipants = async () => {
    setIsLoading(true);
    setMessage("");
    setErrorPopupMessage("");
    setShowErrorPopup(false);
    try {
      const response = await axios.get(`${API_BASE_URL}/participants`, {
        // Ruta /participants
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setParticipants(response.data);
    } catch (err) {
      setErrorPopupMessage(
        err.response?.data?.error || "Error al cargar los participantes."
      );
      setShowErrorPopup(true);
      console.error("Error fetching participants:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Efecto para cargar los participantes al montar el componente o cuando el token cambia
  useEffect(() => {
    if (token) {
      fetchParticipants();
    } else {
      setErrorPopupMessage("No autenticado. Por favor, inicie sesión.");
      setShowErrorPopup(true);
      setIsLoading(false);
    }
  }, [token, API_BASE_URL]);

  // Lógica de Filtrado en Tiempo Real
  const filteredParticipants = useMemo(() => {
    if (!searchTerm) {
      return participants;
    }
    return participants.filter((participant) => {
      // Asegúrate de que la columna exista o proporciona un valor por defecto (ej. '')
      const value = String(participant[searchColumn] || "").toLowerCase();
      return value.includes(searchTerm.toLowerCase());
    });
  }, [participants, searchTerm, searchColumn]);

  // Lógica de Paginación
  const indexOfLastParticipant = currentPage * participantsPerPage;
  const indexOfFirstParticipant = indexOfLastParticipant - participantsPerPage;
  const currentParticipants = filteredParticipants.slice(
    indexOfFirstParticipant,
    indexOfLastParticipant
  );
  const totalPages = Math.ceil(
    filteredParticipants.length / participantsPerPage
  );
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };
  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  // Función para editar un participante
  const handleEditParticipant = (participantId) => {
    navigate(`/admin/participants/edit/${participantId}`);
  };

  // Función para eliminar un participante
  const handleDeleteParticipant = async (participantId) => {
    if (
      !window.confirm(
        "¿Está seguro de que desea eliminar este participante? Esta acción no se puede deshacer y puede afectar certificados asociados."
      )
    ) {
      return;
    }
    setMessage("");
    setErrorPopupMessage("");
    setShowErrorPopup(false);
    setIsLoading(true);

    try {
      await axios.delete(`${API_BASE_URL}/participants/${participantId}`, {
        // Ruta DELETE /participants/:id
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage("Participante eliminado exitosamente.");
      fetchParticipants(); // Recargar la lista
    } catch (err) {
      setErrorPopupMessage(
        err.response?.data?.error || "Error al eliminar el participante."
      );
      setShowErrorPopup(true);
      console.error("Error deleting participant:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Función para cerrar el pop-up de error
  const handleCloseErrorPopup = () => {
    setShowErrorPopup(false);
    setErrorPopupMessage("");
  };

  if (isLoading) {
    return (
      <div className="manage-participants-container">
        <p>Cargando participantes...</p>
      </div>
    );
  }

  // Devuelve un array de páginas a mostrar en la paginación (máximo 7)
  const getVisiblePages = () => {
    const maxVisible = 7;
    let start = Math.max(currentPage - Math.floor(maxVisible / 2), 1);
    let end = start + maxVisible - 1;

    if (end > totalPages) {
      end = totalPages;
      start = Math.max(end - maxVisible + 1, 1);
    }

    const pages = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="manage-participants-container">
      <div className="manage-participants-card">
        <h2 className="manage-participants-title">Gestión de Participantes</h2>
        <p className="manage-participants-description">
          Administra la información de los participantes que han recibido
          certificados.
        </p>

        <div className="top-controls">
          <Link
            to="/admin/participants/create"
            className="create-participant-button"
          >
            <span className="material-icons">add</span> Crear Nuevo Participante
          </Link>
          <div className="search-filter-container">
            <select
              value={searchColumn}
              onChange={(e) => setSearchColumn(e.target.value)}
              className="search-column-select"
            >
              <option value="full_name">Nombre Completo</option>
              <option value="document_number">Número de Documento</option>
              <option value="email">Correo Electrónico</option>
              {/* 'Tipo de Participante' se eliminó de las opciones de búsqueda ya que no se mostrará */}
            </select>
            <input
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="search-input"
            />
          </div>
        </div>

        {message && (
          <p className="manage-participants-message success">{message}</p>
        )}

        {filteredParticipants.length === 0 && searchTerm !== "" ? (
          <p className="no-results-message">
            No se encontraron participantes que coincidan con la búsqueda.
          </p>
        ) : filteredParticipants.length === 0 ? (
          <p className="no-results-message">
            No hay participantes registrados.{" "}
            <Link to="/admin/participants/create">Crea uno ahora</Link>.
          </p>
        ) : (
          <table className="participants-table">
            <thead>
              <tr>
                {/* Se eliminó la columna ID */}
                <th>Documento</th>
                <th>Nombre Completo</th>
                <th>Correo</th>
                <th>Teléfono</th>
                {/* Se eliminó la columna Tipo */}
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {currentParticipants.map((participant) => (
                <tr key={participant.id}>
                  {/* Se eliminó la celda para ID */}
                  <td>{participant.document_number}</td>
                  <td>{participant.full_name}</td>
                  <td>{participant.email || "N/A"}</td>
                  <td>{participant.phone_number || "N/A"}</td>
                  {/* Se eliminó la celda para Tipo */}
                  <td>
                    <div className="action-buttons-container">
                      <button
                        onClick={() => handleEditParticipant(participant.id)}
                        className="action-button edit-button"
                        disabled={isLoading}
                      >
                        <span className="material-icons">edit</span>
                        <span className="button-text">Editar</span>
                      </button>
                      <button
                        onClick={() => handleDeleteParticipant(participant.id)}
                        className="action-button delete-button"
                        disabled={isLoading}
                      >
                        <span className="material-icons">delete</span>
                        <span className="button-text">Eliminar</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Controles de Paginación */}
        {filteredParticipants.length > participantsPerPage && (
          <div className="pagination-controls">
            <button
              onClick={prevPage}
              disabled={currentPage === 1}
              className="pagination-button"
            >
              Anterior
            </button>

            {getVisiblePages().map((page) => (
              <button
                key={page}
                onClick={() => paginate(page)}
                className={`pagination-button ${
                  currentPage === page ? "active" : ""
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={nextPage}
              disabled={currentPage === totalPages}
              className="pagination-button"
            >
              Siguiente
            </button>
          </div>
        )}
      </div>

      {/* Pop-up de Error */}
      {showErrorPopup && (
        <div className="popup-overlay">
          <div className="popup-content error-popup">
            <h3>¡Error!</h3>
            <p>{errorPopupMessage}</p>
            <button
              onClick={handleCloseErrorPopup}
              className="popup-button error-button"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageParticipantsPage;
